import * as RadixMenu from '@radix-ui/react-dropdown-menu';

import { cn } from '@/shared/lib/cn';

export const Menu = RadixMenu.Root;
export const MenuTrigger = RadixMenu.Trigger;

export function MenuContent({
  className,
  align = 'end',
  onCloseAutoFocus,
  container,
  ...props
}: RadixMenu.DropdownMenuContentProps & {
  /** Portal target. Only for an element that is itself fullscreen, where <body> is not shown. */
  container?: HTMLElement | null;
}) {
  return (
    <RadixMenu.Portal container={container}>
      <RadixMenu.Content
        align={align}
        sideOffset={6}
        /*
         * A menu item that opens a dialog leaves two things closing at once. Radix returns
         * focus to the trigger when the menu closes, which yanks the caret out of the dialog
         * the item just opened — measured: focus was on the rename input, and 600ms later it
         * was back on the "Thao tác" button. It also made Escape a coin flip, because whether
         * the keypress reached the dialog depended on which side of that steal it landed.
         * When a dialog has taken over, leave its focus alone; otherwise restore as usual.
         */
        onCloseAutoFocus={(event) => {
          if (document.querySelector('[role="dialog"]')) event.preventDefault();
          onCloseAutoFocus?.(event);
        }}
        className={cn(
          // No focus ring on the panel or its rows: Radix focuses them itself, and the row's
          // highlight already is the focus indicator — a ring on top drew every item twice.
          'z-50 min-w-[11rem] animate-slide-up overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-lg',
          'outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
          className,
        )}
        {...props}
      />
    </RadixMenu.Portal>
  );
}

/**
 * A menu row.
 *
 * `asChild` hands the row off to the element you pass — a <Link>, typically — and Radix then
 * merges its props into that single child. It must therefore receive exactly one child, so the
 * `icon` convenience is not available in that mode: put the icon inside your own element. The
 * types enforce it, because the failure at runtime is not a misplaced icon but
 * "Primitive.div failed to slot onto its children", which takes the whole page down with it.
 */
type MenuItemProps = RadixMenu.DropdownMenuItemProps & { destructive?: boolean } & (
  | { asChild: true; icon?: never }
  | { asChild?: false; icon?: React.ReactNode }
);

export function MenuItem({ className, destructive, icon, children, ...props }: MenuItemProps) {
  return (
    <RadixMenu.Item
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-base outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
        'transition-colors duration-150 data-[highlighted]:bg-surface-hover',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        destructive ? 'text-danger data-[highlighted]:bg-danger-subtle' : 'text-fg',
        className,
      )}
      {...props}
    >
      {props.asChild ? (
        children
      ) : (
        <>
          {icon && <span className="shrink-0 text-fg-subtle">{icon}</span>}
          {children}
        </>
      )}
    </RadixMenu.Item>
  );
}

export function MenuSeparator({ className, ...props }: RadixMenu.DropdownMenuSeparatorProps) {
  return <RadixMenu.Separator className={cn('my-1 h-px bg-border', className)} {...props} />;
}

export function MenuLabel({ className, ...props }: RadixMenu.DropdownMenuLabelProps) {
  return <RadixMenu.Label className={cn('px-2 py-1.5 text-xs text-fg-subtle', className)} {...props} />;
}
